import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { reviewService } from '../services/reviewService';
import { Book } from '../types';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { Button } from './ui/Button';
import { Textarea } from './ui/Textarea';
import Modal from './ui/Modal';

export const ReviewWizard: React.FC<{
  isOpen: boolean; onClose: () => void; book: Book; userId: string; userName: string;
  onDirtyChange?: (dirty: boolean, busy: boolean) => void;
}> = ({ isOpen, onClose, book, userId, userName, onDirtyChange }) => {
  const confirm = useConfirmation();
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const dirty = !!(reviewText || rating);
  useEffect(() => { onDirtyChange?.(isOpen && dirty, isSubmitting); }, [dirty, isOpen, isSubmitting, onDirtyChange]);
  const words = reviewText.trim().split(/\s+/).filter(Boolean).length;
  const close = async () => {
    if (isSubmitting) return;
    if (dirty && !(await confirm({ title: 'Rezension verwerfen?', description: 'Dein Text und die Bewertung sind noch nicht eingereicht und gehen beim Schließen verloren.', confirmLabel: 'Rezension verwerfen', danger: true }))) return;
    setReviewText(''); setRating(null); setError(''); onClose();
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError('');
    if (words < 50) { setError('Bitte schreibe mindestens 50 Wörter.'); return; }
    if (reviewText.length > 2060) { setError('Die Rezension darf höchstens 2.060 Zeichen enthalten.'); return; }
    setIsSubmitting(true);
    try {
      await reviewService.createReview({ autor: userName, autorId: userId, buchtitel: book.titel || '', buchautor: book.autor || '',
        rezensionstext: reviewText.trim(), buchId: book.$id, status: 'published', wortanzahl: words, bewertung: rating });
      setReviewText(''); setRating(null); toast.success('Rezension eingereicht.'); onClose();
    } catch (e) { console.error('Rezension konnte nicht eingereicht werden:', e); setError('Die Rezension konnte nicht eingereicht werden. Dein Text bleibt erhalten. Bitte versuche es erneut.'); }
    finally { setIsSubmitting(false); }
  };
  if (!isOpen) return null;
  return <Modal title="Rezension einreichen" onClose={close} busy={isSubmitting} wide>
    <p className="mb-5 text-sm text-gray-600"><strong>{book.titel}</strong> von {book.autor}</p>
    <form onSubmit={submit} className="space-y-5">
      <fieldset disabled={isSubmitting}><legend className="mb-2 text-sm font-medium text-gray-700">Bewertung — optional</legend>
        <div className="flex gap-2">{[1, 2, 3, 4, 5].map(star => <button key={star} type="button" aria-label={`${star} ${star === 1 ? 'Stern' : 'Sterne'}`} aria-pressed={rating === star} onClick={() => setRating(rating === star ? null : star)} className={`rounded-lg px-2 text-3xl ${rating && rating >= star ? 'text-yellow-500' : 'text-gray-400'}`}>★</button>)}</div>
      </fieldset>
      <Textarea label="Rezensionstext" value={reviewText} onChange={e => setReviewText(e.target.value)} rows={10} maxLength={2060} required disabled={isSubmitting} helperText={`${words} Wörter · ${reviewText.length.toLocaleString('de-DE')} von 2.060 Zeichen. Mindestens 50 Wörter.`} />
      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      <div className="action-row"><Button variant="outline" onClick={close} disabled={isSubmitting}>Abbrechen</Button><Button type="submit" loading={isSubmitting}>Rezension einreichen</Button></div>
    </form>
  </Modal>;
};
